<template>
  <WrapperForm
    :tabs
    v-model="currentTab"
    :customClass="['scrollable', 'scrollbar']"
  >
    <component :is="TABS_COMPONENTS[currentTab]" />
  </WrapperForm>
</template>

<script setup lang="ts">
  import { ref, type Component } from 'vue'
  import { storeToRefs } from 'pinia'
  import ContratContent from '@/features/interface/demande/cpro/steps/contrat/ContratStep.vue'
  import DocumentsContent from '@/features/interface/demande/cpro/steps/documents/DocumentsStep.vue'
  import EmployeurContent from '@/features/interface/demande/cpro/steps/employeur/EmployeurStep.vue'
  import FormationContent from '@/features/interface/demande/cpro/steps/formation/FormationStep.vue'
  import IntroductionContent from '@/features/interface/demande/cpro/steps/introduction/IntroductionStep.vue'
  import SalarieContent from '@/features/interface/demande/cpro/steps/salarie/SalarieStep.vue'
  import SyntheseContent from '@/features/interface/demande/cpro/steps/synthese/SyntheseStep.vue'
  import { useDemandeTabsSummarizer } from '@/features/interface/demande/shared'
  import type { ContratProDTO } from '@/features/api/extranet/dto/Demande/CPRO'
  import { type ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
  import { useDemandeCPROStore } from './useDemandeCPROStore'

  const TabKeys = ['Introduction', 'Employeur', 'Salarié', 'Contrat', 'Formation', 'Synthèse', 'Documents'] as const
  type TabKey = (typeof TabKeys)[number]

  const TABS_COMPONENTS: { [key in TabKey]?: Component } = {
    Introduction: IntroductionContent,
    Employeur: EmployeurContent,
    Salarié: SalarieContent,
    Contrat: ContratContent,
    Formation: FormationContent,
    Synthèse: SyntheseContent,
    Documents: DocumentsContent,
  } as const

  const currentTab = ref(TabKeys[0])

  const config = {
    Introduction: { include: (state) => [state.validationNotice] },
    Employeur: {
      includeDeep: (state) => [state.employeur],
    },
    Salarié: {
      includeDeep: (state) => [state.salarie, state.tutorat],
    },
    Contrat: {
      includeDeep: (state) => [state.contrat],
      include: (state) => [state.dispositif, state.sousDispositif],
    },
    Formation: { includeDeep: (state) => [state.formation] },
    Synthèse: {
      include: (state) => [state.lieuSignature, state.commentaire, state.acceptationCg, state.attestationSurHonneur],
    },
    Documents: {
      includeDeep: (state) => [state.fichiers],
      include: (state) => [state.attestationPiecesJustificatives],
    },
  } satisfies ErreurSummaryConfiguration<ContratProDTO>

  const { meta } = storeToRefs(useDemandeCPROStore())

  const { tabs } = useDemandeTabsSummarizer<ContratProDTO>(config, meta)
</script>
